import { type AnnotationFeedbackCreateRequest, type FeedbackTypeEnum } from 'api';
import { Form, TextField, useForm } from 'form';
import { useTranslation } from 'i18n';
import { addError } from 'monitor';
import { type KeyboardEventHandler, useEffect, useState } from 'react';
import { ActionRow, Button, Drawer, Stack, useBreakpoint } from 'ui';

export type CommentFormValues = Omit<AnnotationFeedbackCreateRequest, 'x_coordinate' | 'y_coordinate'>;

interface CommentFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  comment?: {
    id: number;
    feedback_type: FeedbackTypeEnum;
    detail: string;
  };
  onSave: (data: CommentFormValues) => void;
}

export const CommentFormDrawer = ({ isOpen, onClose, onSave, comment }: CommentFormDrawerProps) => {
  const { t } = useTranslation('features');
  const { isMobile } = useBreakpoint();
  const form = useForm();
  const [feedbackType, setFeedbackType] = useState<'INCORRECT_DETECTION' | 'MISSING_DETECTION' | null>(null);

  const isEditing = Boolean(comment?.id);

  useEffect(() => {
    if (comment?.feedback_type && comment?.detail !== undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFeedbackType(comment.feedback_type as 'INCORRECT_DETECTION' | 'MISSING_DETECTION');
      // eslint-disable-next-line react-hooks/set-state-in-effect
      form.reset({ note: comment.detail });
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFeedbackType(null);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      form.reset({ note: '' });
    }
  }, [comment, form]);

  const handleSave = (values: { note?: string }) => {
    if (!feedbackType) {
      addError(Error('AI feedback submitted without a feedback type'));
      return;
    }

    const data = {
      detail: values.note || '',
      feedback_type: feedbackType,
    };

    onSave(data);

    setFeedbackType(null);
    form.reset({ note: '' });
  };

  const handleNoteKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void form.handleSubmit(handleSave)();
    }
  };

  return (
    <Drawer
      open={isOpen}
      anchor={isMobile ? 'bottom' : 'right'}
      onClose={onClose}
      sx={{
        zIndex: 2000,
      }}
    >
      <Drawer.Header
        title={
          isEditing ? t('annotations.commentFormDrawer.titleEdit') : t('annotations.commentFormDrawer.titleCreate')
        }
      />
      <Form {...form} onSubmit={handleSave}>
        <Stack spacing={2}>
          <Button
            variant={feedbackType === 'INCORRECT_DETECTION' ? 'primary' : 'secondary'}
            onClick={() => setFeedbackType('INCORRECT_DETECTION')}
            fullWidth
          >
            {t('annotations.commentFormDrawer.feedbackType.incorrectDetection')}
          </Button>
          <Button
            variant={feedbackType === 'MISSING_DETECTION' ? 'primary' : 'secondary'}
            onClick={() => setFeedbackType('MISSING_DETECTION')}
            fullWidth
          >
            {t('annotations.commentFormDrawer.feedbackType.missingDetection')}
          </Button>

          {feedbackType ? (
            <TextField
              label={t('annotations.commentFormDrawer.labels.note')}
              name="note"
              multiline
              rows={4}
              onKeyDown={handleNoteKeyDown}
              fullWidth
            />
          ) : null}
        </Stack>

        <ActionRow>
          <Button variant="secondary" onClick={onClose}>
            {t('annotations.commentFormDrawer.actions.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={!feedbackType}>
            {t('annotations.commentFormDrawer.actions.save')}
          </Button>
        </ActionRow>
      </Form>
    </Drawer>
  );
};
